import { Box, FormControl } from '@primer/react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../components/ui/button';
import Modal from '../../../components/ui/modal';
import ModalActions from '../../../components/ui/modal/ModalActions';
import Text from '../../../components/ui/text';
import TextInput from '../../../components/ui/text-input';
import { useCommonContext } from '../../../contexts/common';
import { useCommonSelectedHook } from '../../../hooks/useCommonSelectedHook';
import { ePageBaseStatus } from '../../../models/ePageBaseStatus';
import { INgayNghi } from '../../../models/response/category/INgayNghi';
import { actions } from '../../../state/actions/actionsWrapper';
import { RootState } from '../../../state/reducers';
import { ComboboxTruong } from '../../../components/combobox-truong';

const NgayNghiEditModal = () => {
    const { status, editingData } = useSelector((x: RootState) => x.danhMucWrapper.ngayNghi)
    const { foCaAnTruongsVm } = useSelector((x: RootState) => x.thucDonWrapper.caAnTruong);

    const dispatch = useDispatch();
    const { dm_coso_id } = useCommonSelectedHook();
    const { translate } = useCommonContext();
    const formatDateForInput = (dateString: string | Date) => {
        if (!dateString) return "";
        const date = new Date(dateString + "Z");
        if (isNaN(date.getTime())) return "";
        return date.toISOString().split("T")[0];
    };

    const { register, handleSubmit, setValue, watch, formState: { errors }, control } = useForm<INgayNghi>({
        defaultValues: editingData ? {
            ...editingData,
            from_date: formatDateForInput(editingData.from_date),
            to_date: formatDateForInput(editingData.to_date),
        } : undefined
    })

    const getTruongDisplayText = (dm_truong_id?: number): string => {
        if (!dm_truong_id || !foCaAnTruongsVm) return "";
        const truong = foCaAnTruongsVm.find(x => x.dm_truong_id === dm_truong_id);
        return truong?.ten_truong || "";
    };

    const onSubmit = async (data: any) => {
        dispatch(actions.danhMucWrapper.ngayNghi.SAVE_START({
            ...data,
            id: data?.id ?? 0,
            dm_coso_id: dm_coso_id
        }))
    }

    return (
        <Modal isOpen
            title={editingData ? "Base.Label.Edit" : "Base.Label.AddNew"}
            onClose={() => {
                dispatch(actions.danhMucWrapper.ngayNghi.CLOSE_EDIT_MODAL(undefined))
            }}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: "grid", gap: 2 }}>
                    <FormControl>
                        <FormControl.Label><Text text='Nội dung' /></FormControl.Label>
                        <TextInput block
                            name="noi_dung"
                            register={register}
                            errors={errors}
                            required
                            validateMessage={`${translate("Base.Form.RequiredMessage")} ${translate("nội dung")}`}
                        />

                    </FormControl>
                    <FormControl>
                        <FormControl.Label>
                            <Text text="Thời gian" />
                        </FormControl.Label>
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Text
                                    text="Từ ngày"
                                    sx={{ fontSize: "12px", color: "#656d76", marginBottom: 1 }}
                                />
                                <Controller
                                    name="from_date"
                                    control={control}
                                    rules={{
                                        required: `${translate("Base.Form.RequiredMessage")} Ngày bắt đầu đăng ký`,
                                        validate: {
                                            beforeEndDate: (value: string) => {
                                                const endDate = watch("to_date");
                                                if (value && endDate && new Date(value) > new Date(endDate)) {
                                                    return "Ngày bắt đầu đăng ký phải nhỏ hơn ngày kết thúc đăng ký";
                                                }
                                                return true;
                                            }
                                        }
                                    }}
                                    render={({ field }) => (
                                        <>
                                            <TextInput
                                                block
                                                type="date"
                                                name={field.name}
                                                value={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                sx={{ width: 300 }}
                                            />
                                            {errors.from_date && (
                                                <FormControl.Validation variant="error">
                                                    {errors.from_date.message}
                                                </FormControl.Validation>
                                            )}
                                        </>
                                    )}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Text
                                    text="Đến ngày"
                                    sx={{ fontSize: "12px", color: "#656d76", marginBottom: 1 }}
                                />
                                <Controller
                                    name="to_date"
                                    control={control}
                                    rules={{
                                        required: `${translate("Base.Form.RequiredMessage")} Ngày kết thúc đăng ký`,
                                        validate: {
                                            afterStartDate: (value: string) => {
                                                const startDate = watch("from_date");
                                                if (value && startDate && new Date(value) < new Date(startDate)) {
                                                    return "Ngày kết thúc đăng ký phải lớn hơn ngày bắt đầu đăng ký";
                                                }
                                                return true;
                                            }
                                        }
                                    }}
                                    render={({ field }) => (
                                        <>
                                            <TextInput
                                                block
                                                type="date"
                                                name={field.name}
                                                value={field.value}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                sx={{ width: 300 }}
                                            />
                                            {errors.to_date && (
                                                <FormControl.Validation variant="error">
                                                    {errors.to_date.message}
                                                </FormControl.Validation>
                                            )}
                                        </>
                                    )}
                                />
                            </Box>
                        </Box>
                    </FormControl>
                    <FormControl>
                        <FormControl.Label>
                            <Text text="Chọn cấp học" />
                        </FormControl.Label>
                        <Controller
                            name="dm_truong_id"
                            control={control}
                            rules={!editingData ? {
                                required: "Vui lòng Chọn cấp học",
                                min: { value: 1, message: "Vui lòng Chọn cấp học" }
                            } : {}}
                            render={({ field }) => (
                                <ComboboxTruong
                                    value={field.value}
                                    onValueChanged={field.onChange}
                                    isReadonly={editingData && editingData?.id > 0}
                                />
                            )}
                        />
                        {errors.dm_truong_id && (
                            <FormControl.Validation variant="error">
                                {errors.dm_truong_id.message}
                            </FormControl.Validation>
                        )}
                    </FormControl>
                </Box>
                <ModalActions>
                    <Button text='Base.Label.Close' onClick={() => {
                        dispatch(actions.danhMucWrapper.ngayNghi.CLOSE_EDIT_MODAL(undefined))
                    }} />
                    <Button text={editingData ? "Base.Label.Update" : "Base.Label.AddNew"} variant='primary' type='submit'
                        isLoading={status === ePageBaseStatus.is_saving}
                    />
                </ModalActions>
            </form>

        </Modal>
    );
};

export default NgayNghiEditModal;